import { apiFetch, type TokenStore } from './client.ts';
import { fileToBase64 } from './documents.ts';

/**
 * ABA clinical + encounter API client (spec §8; routes verified against
 * src/routes/aba.routes.ts + encounters.routes.ts). Shapes mirror the LIVE
 * server (raw snake_case rows). The v7 API surface is frozen.
 *
 * Data entries carry a client-generated `idempotencyKey` (the offline contract,
 * §5.15F) so a replay after reconnect dedupes to the same row server-side.
 */

// ── Encounters ──────────────────────────────────────────────────────────────

export type EncounterType =
  | 'therapy_session'
  | 'assessment'
  | 'consultation'
  | 'supervision'
  | 'other';

/** A user who recorded data (provenance label - display name only). */
export interface RecordedByRef {
  id: string;
  displayName: string;
}

export interface EncounterSummary {
  id: string;
  encounterType: string;
  status: string;
  startedAt: number;
  endedAt: number | null;
  notes: string | null;
  conductedByUserId: string;
  conductedByName: string;
  /** Non-null exactly when this session was logged retrospectively - pairs
   *  with startedAt (when it happened, declared) vs the entry timestamp. */
  backdatedReason: string | null;
  completedLate: boolean;
  /** Who took data in this session, in first-entry order. */
  recordedBy: RecordedByRef[];
  /** Total recorded entries in the session. */
  entryCount: number;
}

export async function listEncounters(caseId: string, store: TokenStore): Promise<EncounterSummary[]> {
  const res = await apiFetch<{ encounters: EncounterSummary[] }>(
    `/cases/${encodeURIComponent(caseId)}/encounters`,
    { method: 'GET' },
    store,
  );
  return res.encounters;
}

/** One encounter's lifecycle state - used by the session screens to restore
 *  their phase after a reload/revisit (2026-07-06 P0-B). */
export interface EncounterDetail {
  id: string;
  caseId: string;
  encounterType: string;
  status: 'in_progress' | 'completed' | 'discarded';
  conductedByUserId: string;
  startedAt: number;
  endedAt: number | null;
  notes: string | null;
  /** Non-null exactly when this session was logged retrospectively. */
  backdatedReason: string | null;
  /** created_at is still inside the practice's session edit window. */
  withinEditWindow: boolean;
  editWindowClosesAt: number;
  savesLate: boolean;
  completedLate: boolean;
  lastActivityAt: number | null;
  /** Server-computed: this viewer may complete the session (status + conductor). */
  canComplete: boolean;
  /** Server-computed: this viewer may discard the session (draft vs record rules). */
  canDiscard: boolean;
  /** Role-capability: this viewer may request an edit once the session is complete. */
  canRequestEdit: boolean;
  /** Who ran the session (display name). Only they may ask to reopen it, so a
   *  viewer who is not the conductor is told whose request it has to be. */
  conductedByName: string;
  /** Server-computed: this viewer could approve/reject a pending edit request. */
  canResolveEditRequest: boolean;
  /** When this record becomes FINAL — voiding and edit requests both close
   *  (owner decision 2026-08-20). Null while it is not a locked record. */
  finalAt: number | null;
}

export async function getEncounter(encounterId: string, store: TokenStore): Promise<EncounterDetail> {
  const res = await apiFetch<{ encounter: EncounterDetail }>(
    `/encounters/${encodeURIComponent(encounterId)}`,
    { method: 'GET' },
    store,
  );
  return res.encounter;
}

export async function startEncounter(
  caseId: string,
  encounterType: EncounterType,
  store: TokenStore,
  /** Log a past session: when it actually happened + why it's being entered
   *  late. Omit for the ordinary, live-session path. */
  backdated?: { occurredAt: number; reason: string },
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/cases/${encodeURIComponent(caseId)}/encounters`,
    { method: 'POST', body: JSON.stringify({ encounterType, ...backdated }) },
    store,
  );
}

export interface EncounterDataEntry {
  id: string;
  /** The recorder's own key for this tap - what deleteDataEntry is keyed on. */
  idempotencyKey: string;
  targetId: string;
  targetName: string;
  subTargetId: string | null;
  subTargetLabel: string | null;
  dataType: string;
  valueJson: string;
  recordedBy: RecordedByRef;
  recordedAt: number;
}

export interface EncounterBehaviourEvent {
  id: string;
  behaviourTargetId: string;
  behaviourTargetName: string;
  occurredAt: number;
  durationSeconds: number | null;
  latencySeconds: number | null;
  intensity: string | null;
  count: number;
  antecedent: string | null;
  behaviourDescription: string | null;
  consequence: string | null;
  recordedBy: RecordedByRef;
  recordedAt: number;
}

export interface EncounterAbcEntry {
  id: string;
  antecedents: string[];
  behaviour: string | null;
  consequences: string[];
  possibleFunction: string | null;
  occurredAt: number;
  recordedBy: RecordedByRef;
  recordedAt: number;
}

export interface EncounterEntries {
  dataEntries: EncounterDataEntry[];
  behaviourEvents: EncounterBehaviourEvent[];
  abcEntries?: EncounterAbcEntry[];
}

export async function getEncounterEntries(
  encounterId: string,
  store: TokenStore,
): Promise<EncounterEntries> {
  const res = await apiFetch<{ entries: EncounterEntries }>(
    `/encounters/${encodeURIComponent(encounterId)}/entries`,
    { method: 'GET' },
    store,
  );
  return res.entries;
}

export async function completeEncounter(
  encounterId: string,
  store: TokenStore,
  body: { notes?: string; endedAt: number },
): Promise<void> {
  const notes = body.notes?.trim();
  await apiFetch<{ ok: boolean }>(
    `/encounters/${encodeURIComponent(encounterId)}/complete`,
    { method: 'POST', body: JSON.stringify({ ...(notes ? { notes } : {}), endedAt: body.endedAt }) },
    store,
  );
}

/**
 * Request an edit to a completed encounter (invariant 6 - reason required).
 * A completed session is immutable until the request is approved.
 */
export async function createEditRequest(
  encounterId: string,
  reason: string,
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/encounters/${encodeURIComponent(encounterId)}/edit-requests`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

// ── Skill targets ─────────────────────────────────────────────────────────────

export type AbaDataType =
  | 'yn'
  | 'trial_percentage'
  | 'task_analysis'
  | 'prompt_hierarchy'
  | 'duration'
  | 'frequency';

export type SubTargetStatus = 'waiting' | 'active' | 'mastered' | 'on_hold' | 'discontinued';

export interface SubTarget {
  id: string;
  targetId: string;
  label: string;
  status: string;
}

export interface SkillTarget {
  id: string;
  caseId: string;
  area: string;
  name: string;
  dataType: AbaDataType;
  status: string;
  masteryCriterionJson?: string | null;
  /** The target's mastery rule is met by its data but it is not yet mastered: the
   *  practitioner is prompted to confirm with "Mark mastered". */
  masteryReady?: boolean;
  /** Trailing session means (max 10) for the card's at-rest trend sparkline. */
  recentPoints?: Array<{ recordedAt: number; value: number }>;
  /**
   * Server-computed: true once ANY reading exists, from the same rows the write guard
   * counts. Absent = treat as recorded, so the control fails closed on an old payload.
   */
  hasRecordedData?: boolean;
  teachingNotes?: string | null;
  subTargets?: SubTarget[];
  // Optimistic-concurrency version (F15): updated_at, or created_at if never edited.
  createdAt?: number;
  updatedAt?: number | null;
}

export interface SkillTargetListResponse {
  targets: SkillTarget[];
  /**
   * ADR 0006 - may this actor move a sub-target's status along? A clinician
   * always may; a tutor or client data-taker only when the case delegates
   * progression AND they hold data-entry sign-off. Decided server-side with the
   * same can() the PATCH runs, so the control and the write can never disagree
   * (v7.79 capability-flag pattern).
   */
  canProgressSubTargets: boolean;
  /** Denial code when canProgressSubTargets is false; null when allowed. */
  subTargetProgressionDenialCode: string | null;
  /**
   * May this actor author targets (create / edit / mastery / discontinue / add
   * sub-target / library)? Needs owner|practitioner AND a clinical CASE role.
   */
  canAuthorTargets: boolean;
  /** May they CREATE (target / sub-target / apply-from-library)? Adds "case is active". */
  canCreateTargets: boolean;
  /** May they save to the PRACTICE library? Practice role only - no case role. */
  canManageTargetLibrary: boolean;
}

/**
 * Returns the whole response, not just `targets` - the capability flags travel
 * with the list, and all three callers share the `['skill-targets', caseId]`
 * query key, so they must share one shape.
 */
export async function listSkillTargets(
  caseId: string,
  store: TokenStore,
): Promise<SkillTargetListResponse> {
  return apiFetch<SkillTargetListResponse>(
    `/cases/${encodeURIComponent(caseId)}/aba/targets`,
    {},
    store,
  );
}

export interface CreateSkillTargetBody {
  area: string;
  name: string;
  dataType?: AbaDataType;
  teachingNotes?: string;
  /** Steps created with the target, in the same transaction. */
  subTargets?: string[];
}

export async function createSkillTarget(
  caseId: string,
  body: CreateSkillTargetBody,
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/cases/${encodeURIComponent(caseId)}/aba/targets`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

// ── Data entries (offline-first) ───────────────────────────────────────────────

export interface RecordDataEntryBody {
  targetId: string;
  subTargetId?: string;
  dataType?: string;
  valueJson: string;
  clientTs?: number;
  idempotencyKey: string;
}

/**
 * Record an ABA data entry against an encounter. 201 on first write, 200 on
 * idempotent replay (same idempotencyKey) - both return the same `{ id }`.
 */
export async function recordDataEntry(
  encounterId: string,
  body: RecordDataEntryBody,
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/encounters/${encodeURIComponent(encounterId)}/aba/data-entries`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

export async function deleteDataEntry(
  encounterId: string,
  idemKey: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<void>(
    `/encounters/${encodeURIComponent(encounterId)}/aba/data-entries/by-idem-key/${encodeURIComponent(idemKey)}`,
    { method: 'DELETE' },
    store,
  );
}

// ── Progress (per-target data visualisation, gap 16-A) ──────────────────────────

export interface TargetProgressPoint {
  encounterId: string;
  /** epoch ms of the earliest entry in the session */
  recordedAt: number;
  /** session mean of the recorded values (yn → % correct) */
  value: number;
  /** number of entries averaged into this point */
  n: number;
  /** distinct recorders whose entries feed this point, in first-entry order */
  recordedBy: RecordedByRef[];
}

/**
 * Phase change line (single-case design convention): 'solid' = condition
 * change (e.g. baseline → intervention), 'dashed' = change within a condition.
 */
export interface ProgressPhaseLine {
  id: string;
  label: string;
  lineStyle: 'solid' | 'dashed';
  /** epoch ms the change took effect */
  occurredAt: number;
}

export interface TargetProgress {
  targetId: string;
  dataType: AbaDataType;
  unit: string;
  points: TargetProgressPoint[];
  phaseLines: ProgressPhaseLine[];
}

/**
 * Fetch a skill target's progress series. GET /aba/targets/:id/progress
 * Pass `subTargetId` to chart only the entries recorded against one sub-target.
 */
export async function getTargetProgress(
  targetId: string,
  store: TokenStore,
  subTargetId?: string,
): Promise<TargetProgress> {
  const query = subTargetId ? `?subTargetId=${encodeURIComponent(subTargetId)}` : '';
  return apiFetch<TargetProgress>(
    `/aba/targets/${encodeURIComponent(targetId)}/progress${query}`,
    {},
    store,
  );
}

/** Fetch a behaviour target's progress series. GET /aba/behaviour-targets/:id/progress */
export interface BehaviourTargetProgress {
  targetId: string;
  measurementType: BehaviourTargetMeasurementType;
  unit: string;
  points: TargetProgressPoint[];
  phaseLines: ProgressPhaseLine[];
}

export async function getBehaviourTargetProgress(
  targetId: string,
  store: TokenStore,
): Promise<BehaviourTargetProgress> {
  return apiFetch<BehaviourTargetProgress>(
    `/aba/behaviour-targets/${encodeURIComponent(targetId)}/progress`,
    {},
    store,
  );
}

/**
 * Add a phase change line to a target's chart.
 * POST /aba/targets/:id/phase-lines or /aba/behaviour-targets/:id/phase-lines
 */
export async function addPhaseLine(
  targetKind: 'skill' | 'behaviour',
  targetId: string,
  input: { label: string; lineStyle: 'solid' | 'dashed'; occurredAt?: number },
  store: TokenStore,
): Promise<{ id: string }> {
  const base = targetKind === 'skill' ? '/aba/targets' : '/aba/behaviour-targets';
  return apiFetch<{ id: string }>(
    `${base}/${encodeURIComponent(targetId)}/phase-lines`,
    { method: 'POST', body: JSON.stringify(input) },
    store,
  );
}

/** Remove a phase change line. DELETE /aba/phase-lines/:id */
export async function deletePhaseLine(phaseLineId: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/phase-lines/${encodeURIComponent(phaseLineId)}`,
    { method: 'DELETE' },
    store,
  );
}

// ── Skill-target lifecycle ─────────────────────────────────────────────────────

/** Edit a skill target (name / area / teaching notes). PATCH /aba/targets/:id */
export async function updateSkillTarget(
  targetId: string,
  body: { area?: string; name?: string; teachingNotes?: string | null; dataType?: AbaDataType; expectedUpdatedAt?: number },
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/targets/${encodeURIComponent(targetId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    store,
  );
}

/** Discontinue a skill target (reason required). POST /aba/targets/:id/discontinue */
export async function discontinueSkillTarget(
  targetId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/targets/${encodeURIComponent(targetId)}/discontinue`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

/** Delete a skill target that has no recorded data (directly or via a sub-target).
 *  Refuses SKILL_TARGET_HAS_DATA once any exists — discontinue it instead.
 *  DELETE /aba/targets/:id */
export async function deleteSkillTarget(targetId: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/targets/${encodeURIComponent(targetId)}`,
    { method: 'DELETE' },
    store,
  );
}

/** Move a skill target through its lifecycle (master / hold / reactivate).
 *  'discontinued' is the separate discontinue action. Reactivating a discontinued
 *  target requires a reason (server-enforced). POST /aba/targets/:id/status */
export async function setSkillTargetStatus(
  targetId: string,
  status: 'active' | 'mastered' | 'on_hold',
  store: TokenStore,
  reason?: string,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/targets/${encodeURIComponent(targetId)}/status`,
    { method: 'POST', body: JSON.stringify({ status, ...(reason ? { reason } : {}) }) },
    store,
  );
}

/** Set/replace a skill target's mastery criterion. PUT /aba/targets/:id/mastery-criteria */
export async function setMasteryCriteria(
  targetId: string,
  criterionJson: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/targets/${encodeURIComponent(targetId)}/mastery-criteria`,
    { method: 'PUT', body: JSON.stringify({ criterionJson }) },
    store,
  );
}

// ── Sub-targets ────────────────────────────────────────────────────────────────

/** Add a sub-target. `afterSubTargetId` inserts below that step; omitted appends. */
export async function addSubTargets(
  targetId: string,
  names: readonly string[],
  store: TokenStore,
  afterSubTargetId?: string,
): Promise<{ id: string; ids: string[] }> {
  return apiFetch<{ id: string; ids: string[] }>(
    `/aba/targets/${encodeURIComponent(targetId)}/sub-targets`,
    { method: 'POST', body: JSON.stringify({ names, ...(afterSubTargetId ? { afterSubTargetId } : {}) }) },
    store,
  );
}

export async function deleteSubTarget(subTargetId: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/sub-targets/${encodeURIComponent(subTargetId)}`,
    { method: 'DELETE' },
    store,
  );
}

export async function reorderSubTargets(
  targetId: string,
  orderedSubTargetIds: readonly string[],
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/targets/${encodeURIComponent(targetId)}/sub-target-order`,
    { method: 'PUT', body: JSON.stringify({ orderedSubTargetIds }) },
    store,
  );
}

/** Edit a sub-target (label and/or status). PATCH /aba/sub-targets/:id */
export async function updateSubTarget(
  subTargetId: string,
  body: { label?: string; status?: SubTargetStatus },
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/sub-targets/${encodeURIComponent(subTargetId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    store,
  );
}

// ── ABC entries ────────────────────────────────────────────────────────────────

export type AbcDurationBand = 'under_1_min' | '1_to_5_min' | 'over_5_min';
export type AbcIntensityBand = 'mild' | 'moderate' | 'severe';

/** One ABC entry as the form sends it, wherever it is recorded. */
export interface AbcEntryFields {
  antecedents: string[];
  behaviour: string;
  consequences: string[];
  possibleFunction?: string;
  setting?: string;
  durationBand?: AbcDurationBand;
  intensityBand?: AbcIntensityBand;
  othersPresent: string[];
  note?: string;
  harmReported: boolean;
  behaviourTargetId?: string;
}

export type RecordAbcEntryBody = AbcEntryFields & { occurredAt?: number; idempotencyKey?: string };

/** Record an ABC entry in a session. */
export async function recordAbcEntry(
  encounterId: string,
  body: RecordAbcEntryBody,
  store: TokenStore,
): Promise<{ id: string; notified: number }> {
  return apiFetch<{ id: string; notified: number }>(
    `/encounters/${encodeURIComponent(encounterId)}/aba/abc-entries`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

/** Record an ABC entry with no session (TF-0031). */
export async function recordBetweenSessionAbcEntry(
  caseId: string,
  body: AbcEntryFields & { occurredAt: number; idempotencyKey?: string },
  store: TokenStore,
): Promise<{ id: string; notified: number }> {
  return apiFetch<{ id: string; notified: number }>(
    `/cases/${encodeURIComponent(caseId)}/aba/abc-entries`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

/** Undo a between-session entry its recorder saved moments ago. */
export async function undoAbcEntry(caseId: string, entryId: string, store: TokenStore): Promise<void> {
  await apiFetch<void>(
    `/cases/${encodeURIComponent(caseId)}/aba/abc-entries/${encodeURIComponent(entryId)}`,
    { method: 'DELETE' },
    store,
  );
}

/** Correct a between-session entry: a new version; the original stays readable. */
export async function correctAbcEntry(
  caseId: string,
  entryId: string,
  body: AbcEntryFields & { occurredAt: number; reason: string },
  store: TokenStore,
): Promise<{ id: string; notified: number }> {
  return apiFetch<{ id: string; notified: number }>(
    `/cases/${encodeURIComponent(caseId)}/aba/abc-entries/${encodeURIComponent(entryId)}/correct`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

/** Withdraw a between-session entry that should not exist: kept, and left out of summaries. */
export async function withdrawAbcEntry(caseId: string, entryId: string, reason: string, store: TokenStore): Promise<void> {
  await apiFetch<void>(
    `/cases/${encodeURIComponent(caseId)}/aba/abc-entries/${encodeURIComponent(entryId)}/withdraw`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

export interface AbcEntry {
  id: string;
  encounter_id: string | null;
  antecedents: string[];
  behaviour: string | null;
  consequences: string[];
  possible_function: string | null;
  occurred_at: number;
  setting: string | null;
  duration_band: AbcDurationBand | null;
  intensity_band: AbcIntensityBand | null;
  others_present: string[];
  note: string | null;
  harm_reported: number;
  behaviour_target_id: string | null;
  recorder_case_role: string | null;
  corrects_entry_id: string | null;
  correction_reason: string | null;
  corrected_by_entry_id: string | null;
  withdrawn_at: number | null;
  withdraw_reason: string | null;
  recorded_by: string;
  recorded_by_name: string;
  recorded_at: number;
  /** The server's answer for THIS viewer. */
  can_undo: boolean;
  can_correct: boolean;
  can_withdraw: boolean;
}

/** List a case's ABC entries, newest occurrence first. */
export async function listAbcEntries(
  caseId: string,
  store: TokenStore,
): Promise<{ entries: AbcEntry[] }> {
  return apiFetch<{ entries: AbcEntry[] }>(
    `/cases/${encodeURIComponent(caseId)}/aba/abc-entries`,
    {},
    store,
  );
}

// ── Discard encounter ──────────────────────────────────────────────────────────

/** Discard an in-progress encounter. Reason is required (invariant 6). */
export async function discardEncounter(
  encounterId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/encounters/${encodeURIComponent(encounterId)}/discard`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

// ── Behaviour events ───────────────────────────────────────────────────────────

export interface RecordBehaviourEventBody {
  behaviourTargetId: string;
  occurredAt: number;
  durationSeconds?: number;
  latencySeconds?: number;
  intensity?: string;
  count?: number;
  antecedent?: string;
  behaviourDescription?: string;
  consequence?: string;
  clientTs?: number;
  idempotencyKey: string;
  encounterId?: string;
}

/** Record a behaviour event. 201 on first write, 200 on idempotent replay. */
export async function recordBehaviourEvent(
  body: RecordBehaviourEventBody,
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    '/aba/behaviour-events',
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

// ── Behaviour targets ──────────────────────────────────────────────────────────

export interface BehaviourTarget {
  id: string;
  name: string;
  status: string;
  // Full row fields returned by the list endpoint (used to prefill the edit form).
  operational_definition?: string;
  examples?: string | null;
  non_examples?: string | null;
  severity?: BehaviourTargetSeverity;
  measurement_type?: BehaviourTargetMeasurementType;
  hypothesised_function?: BehaviourTargetFunction;
  instruction_notes?: string | null;
  /** Trailing session values (max 10) for the card's at-rest trend sparkline. */
  recentPoints?: Array<{ recordedAt: number; value: number }>;
  /**
   * Server-computed: true once ANY event exists, counted the way the write guard counts —
   * a discarded encounter's retained events included. Absent = treat as recorded, so the
   * control fails closed on an older payload.
   */
  hasRecordedData?: boolean;
  // Optimistic-concurrency version (F15): updated_at, or created_at if never edited.
  created_at?: number;
  updated_at?: number | null;
}

/** List behaviour targets for a case. GET /cases/:id/aba/behaviour-targets */
export interface BehaviourTargetListResponse {
  targets: BehaviourTarget[];
  /**
   * May this actor author behaviour targets (create / edit / discontinue /
   * apply-from-library)? Needs owner|practitioner AND a clinical CASE role -
   * an un-credentialed lead joins their own case as `observer` (ADR 0003
   * Phase 1), so a practice-role-only test in the UI would offer controls the
   * server refuses. Decided server-side (v7.79 capability-flag pattern).
   */
  canAuthorTargets: boolean;
  /** May they CREATE / apply-from-library? Adds "case is active". */
  canCreateTargets: boolean;
}

export async function listBehaviourTargets(
  caseId: string,
  store: TokenStore,
): Promise<BehaviourTargetListResponse> {
  return apiFetch<BehaviourTargetListResponse>(
    `/cases/${encodeURIComponent(caseId)}/aba/behaviour-targets`,
    {},
    store,
  );
}

export type BehaviourTargetSeverity = 'mild' | 'moderate' | 'severe' | 'crisis';
export type BehaviourTargetMeasurementType =
  | 'frequency'
  | 'duration'
  | 'latency'
  | 'intensity'
  | 'interval_partial'
  | 'interval_whole'
  | 'momentary_time_sample';
export type BehaviourTargetFunction =
  | 'escape'
  | 'attention'
  | 'tangible'
  | 'sensory'
  | 'multiple'
  | 'unknown';

/** UI labels for each measurement type (shared by staff + portal session screens). */
export const MEASUREMENT_LABELS: Record<BehaviourTargetMeasurementType, string> = {
  frequency: 'Frequency',
  duration: 'Duration',
  latency: 'Latency',
  intensity: 'Intensity',
  interval_partial: 'Partial interval',
  interval_whole: 'Whole interval',
  momentary_time_sample: 'Momentary sample',
};

export interface CreateBehaviourTargetBody {
  name: string;
  operationalDefinition: string;
  examples?: string;
  nonExamples?: string;
  severity: BehaviourTargetSeverity;
  measurementType: BehaviourTargetMeasurementType;
  hypothesisedFunction: BehaviourTargetFunction;
  instructionNotes?: string;
}

/** Create a behaviour target. POST /cases/:id/aba/behaviour-targets */
export async function createBehaviourTarget(
  caseId: string,
  body: CreateBehaviourTargetBody,
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/cases/${encodeURIComponent(caseId)}/aba/behaviour-targets`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

/** Editable fields on a behaviour target. PATCH /aba/behaviour-targets/:id */
export interface UpdateBehaviourTargetBody {
  name?: string;
  operationalDefinition?: string;
  severity?: BehaviourTargetSeverity;
  measurementType?: BehaviourTargetMeasurementType;
  hypothesisedFunction?: BehaviourTargetFunction;
  instructionNotes?: string | null;
  /** Optimistic-concurrency token (F15): the updated_at this edit was loaded from. */
  expectedUpdatedAt?: number;
}

/** Edit a behaviour target (definition, severity, measurement, function). */
export async function updateBehaviourTarget(
  targetId: string,
  body: UpdateBehaviourTargetBody,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/behaviour-targets/${encodeURIComponent(targetId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    store,
  );
}

/** Discontinue a behaviour target. Reason required (inv-6). POST /aba/behaviour-targets/:id/discontinue */
export async function discontinueBehaviourTarget(
  targetId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/behaviour-targets/${encodeURIComponent(targetId)}/discontinue`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

// ── FBA ────────────────────────────────────────────────────────────────────────

export type FbaAssessmentType = 'indirect' | 'descriptive' | 'direct' | 'combined';

export interface CreateFbaBody {
  behaviourTargetId: string;
  assessmentType: FbaAssessmentType;
  conductedOn: number;
  hypothesis: string;
  instrumentsJson?: string;
  evidenceSummary?: string;
}

/** Create a functional behaviour assessment. POST /cases/:id/aba/functional-assessments */
export async function createFba(
  caseId: string,
  body: CreateFbaBody,
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/cases/${encodeURIComponent(caseId)}/aba/functional-assessments`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

export interface FbaView {
  id: string;
  behaviour_target_id: string;
  behaviour_target_name: string | null;
  assessment_type: FbaAssessmentType;
  conducted_by: string;
  conducted_by_name: string;
  conducted_on: number;
  hypothesis: string;
  evidence_summary: string | null;
  instruments_json: string | null;
  created_at: number;
  report_document_id: string | null;
  /** The linked document's own status, or null when report_document_id is null.
   *  Mirrors BipView.document_status - a LIVE link is what blocks a new one. */
  document_status: 'draft' | 'pending_review' | 'issued' | 'superseded' | 'withdrawn' | null;
  voided_at: number | null;
  voided_by: string | null;
  voided_by_name: string | null;
  void_reason: string | null;
  /** Whether THIS FBA (not the case as a whole) has a granted client-approval
   *  consent (FB-0055) — a case may hold several concurrent FBAs. */
  consentGranted: boolean;
  /** Durable: was this FBA EVER approved — use this, not consentGranted, to gate
   *  editing (matches the server's fbaAlreadyApproved, which survives a revoke). */
  everApproved: boolean;
}

/** List a case's saved FBAs (newest first) + whether the actor can edit/void/consent.
 *  Each assessment carries its own consentGranted. GET /cases/:id/aba/functional-assessments */
export async function listFbas(caseId: string, store: TokenStore): Promise<{ fbas: FbaView[]; canManage: boolean; canConsent: boolean }> {
  return apiFetch<{ fbas: FbaView[]; canManage: boolean; canConsent: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/aba/functional-assessments`,
    {},
    store,
  );
}

export interface UpdateFbaBody {
  behaviourTargetId?: string;
  assessmentType?: FbaAssessmentType;
  conductedOn?: number;
  hypothesis?: string;
  evidenceSummary?: string | null;
}

/** Edit a saved FBA. PATCH /aba/fbas/:id */
export async function updateFba(
  fbaId: string,
  body: UpdateFbaBody,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/fbas/${encodeURIComponent(fbaId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    store,
  );
}

/** Void a saved FBA. Reason required (inv-6). DELETE /aba/fbas/:id */
export async function voidFba(
  fbaId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/fbas/${encodeURIComponent(fbaId)}`,
    { method: 'DELETE', body: JSON.stringify({ reason }) },
    store,
  );
}

/**
 * Generate the FBA document for an assessment - a snapshot of the assessment
 * and its behaviour target, linked back to the record. POST /aba/fbas/:id/document
 */
export async function generateFbaDocument(
  fbaId: string,
  store: TokenStore,
): Promise<{ documentId: string }> {
  return apiFetch<{ documentId: string }>(
    `/aba/fbas/${encodeURIComponent(fbaId)}/document`,
    { method: 'POST', body: JSON.stringify({}) },
    store,
  );
}

/**
 * Attach an externally-authored FBA file to an assessment. Issued on arrival -
 * it was authored outside HapMetrix, so no internal review ceremony applies.
 * POST /aba/fbas/:id/document/upload
 */
export async function uploadFbaDocument(
  fbaId: string,
  input: { title: string; file: File },
  store: TokenStore,
): Promise<{ documentId: string; fileObjectId: string }> {
  const contentBase64 = await fileToBase64(input.file);
  return apiFetch<{ documentId: string; fileObjectId: string }>(
    `/aba/fbas/${encodeURIComponent(fbaId)}/document/upload`,
    {
      method: 'POST',
      body: JSON.stringify({
        title: input.title,
        filename: input.file.name,
        mimeType: input.file.type,
        contentBase64,
      }),
    },
    store,
  );
}

// ── BIP ────────────────────────────────────────────────────────────────────────

export interface CreateBipBody {
  behaviourTargetId: string;
  fbaId?: string;
  planJson?: string;
}

/** Create a behaviour intervention plan. POST /cases/:id/aba/bips */
export async function createBip(
  caseId: string,
  body: CreateBipBody,
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/cases/${encodeURIComponent(caseId)}/aba/bips`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

export interface BipView {
  id: string;
  behaviour_target_id: string;
  behaviour_target_name: string | null;
  version: number;
  authored_by: string;
  authored_by_name: string;
  fba_id: string | null;
  plan_json: string | null;
  status: 'draft' | 'pending_consent' | 'active' | 'superseded' | 'withdrawn';
  active_from: number | null;
  created_at: number;
  /**
   * The BSP document representing this plan, once one has been generated or
   * uploaded. The column has existed since the baseline schema but was only
   * wired up in 2026-07 - plans created before then have null here.
   */
  document_id: string | null;
  /**
   * The linked document's own status, or null when document_id is null. A LIVE
   * link (anything but 'withdrawn'/'superseded') is what actually blocks
   * generating/uploading a new one server-side - a discarded or withdrawn
   * document is history, not a claim on the plan.
   */
  document_status: 'draft' | 'pending_review' | 'issued' | 'superseded' | 'withdrawn' | null;
  /** Whether THIS plan (not the case as a whole) has a granted consent — per-document
   *  since 2026-08-30, a case may hold several concurrent BSPs. */
  consentGranted: boolean;
  /** Whether a tutor has given their read-and-understood acknowledgement of this
   *  plan's issued document (FB-0055). False when no document exists yet. */
  acknowledgedByTutor: boolean;
  /** Whether THIS actor (the tutor case role) may acknowledge this plan right now —
   *  only once its document is issued. Display only; the POST re-checks. */
  canAcknowledge: boolean;
}

/** List a case's saved BSPs (newest first) + whether the actor can edit/withdraw/consent.
 *  Each plan carries its own consentGranted/acknowledgedByTutor. GET /cases/:id/aba/bips */
export async function listBips(caseId: string, store: TokenStore): Promise<{ bips: BipView[]; canManage: boolean; canConsent: boolean }> {
  return apiFetch<{ bips: BipView[]; canManage: boolean; canConsent: boolean }>(
    `/cases/${encodeURIComponent(caseId)}/aba/bips`,
    {},
    store,
  );
}

/** Edit a saved BSP's plan notes. PATCH /aba/bips/:id */
export async function updateBip(
  bipId: string,
  planJson: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/bips/${encodeURIComponent(bipId)}`,
    { method: 'PATCH', body: JSON.stringify({ planJson }) },
    store,
  );
}

/** Activate a BIP. POST /aba/bips/:id/activate */
export async function activateBip(
  bipId: string,
  clientConsentId: string | undefined,
  store: TokenStore,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/aba/bips/${encodeURIComponent(bipId)}/activate`,
    { method: 'POST', body: JSON.stringify(clientConsentId ? { clientConsentId } : {}) },
    store,
  );
}

/**
 * Generate the BSP document for a plan - a snapshot of the plan and its
 * behaviour target, linked back to the plan. POST /aba/bips/:id/document
 */
export async function generateBipDocument(
  bipId: string,
  store: TokenStore,
): Promise<{ documentId: string }> {
  return apiFetch<{ documentId: string }>(
    `/aba/bips/${encodeURIComponent(bipId)}/document`,
    { method: 'POST', body: JSON.stringify({}) },
    store,
  );
}

/**
 * Attach an externally-authored BSP file to a plan. Issued on arrival - it was
 * authored outside HapMetrix, so no internal review ceremony applies.
 * POST /aba/bips/:id/document/upload
 */
export async function uploadBipDocument(
  bipId: string,
  input: { title: string; file: File },
  store: TokenStore,
): Promise<{ documentId: string; fileObjectId: string }> {
  const contentBase64 = await fileToBase64(input.file);
  return apiFetch<{ documentId: string; fileObjectId: string }>(
    `/aba/bips/${encodeURIComponent(bipId)}/document/upload`,
    {
      method: 'POST',
      body: JSON.stringify({
        title: input.title,
        filename: input.file.name,
        mimeType: input.file.type,
        contentBase64,
      }),
    },
    store,
  );
}

/** Withdraw a BIP. Reason required (inv-6). POST /aba/bips/:id/withdraw */
export async function withdrawBip(
  bipId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/aba/bips/${encodeURIComponent(bipId)}/withdraw`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

// ── Incidents ──────────────────────────────────────────────────────────────────

export type IncidentSeverity = 'minor' | 'moderate' | 'major' | 'critical';

export interface CreateIncidentBody {
  occurredAt: number;
  severity: IncidentSeverity;
  description: string;
  behaviourTargetId?: string;
  injuries?: string;
  restrictivePracticeUsed?: boolean;
  safeguardingFlag?: boolean;
}

/** Log a clinical incident. POST /cases/:id/incidents */
export async function createIncident(
  caseId: string,
  body: CreateIncidentBody,
  store: TokenStore,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/cases/${encodeURIComponent(caseId)}/incidents`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
}

export interface IncidentView {
  id: string;
  occurred_at: number;
  reported_by: string;
  reported_by_name: string;
  behaviour_target_id: string | null;
  behaviour_target_name: string | null;
  severity: IncidentSeverity;
  description: string;
  injuries: string | null;
  restrictive_practice_used: number;
  safeguarding_flag: number;
  created_at: number;
}

/** List a case's logged incidents (newest first). GET /cases/:id/incidents */
export async function listIncidents(caseId: string, store: TokenStore): Promise<{ incidents: IncidentView[] }> {
  return apiFetch<{ incidents: IncidentView[] }>(
    `/cases/${encodeURIComponent(caseId)}/incidents`,
    {},
    store,
  );
}
